import { BranchofficeData } from '@tymbe/schema/branchoffice.interface';
import {
  ActionIcon,
  AvIcon,
  CommunicationIcon,
  CustomIcon,
  DeviceIcon,
  EditorIcon,
  FavouriteIcon,
  ImageIcon,
  MapsIcon,
  PlacesIcon,
} from '@tymbe/ui-icons';
import feathersClient from '../../../../apiClient';
import { ArrayField, Form, FormApi, FormState, FormStateAccessor } from 'informed';
import { useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import ShiftPublishButton from './ShiftPublishButton';
import { TyDateMultiPicker, TyInput, TyNumberInput, TyOption, TySelectInput, TyTimeInput } from '@tymbe/ui-forms';
import moment from 'moment/moment';
import TyCheckbox from '../../../../components/inputs/TyCheckbox';
import { Query } from '@feathersjs/feathers';
import getResponseArray from '@tymbe/utils/getResponseArray';
import { Button, Collapse, SecondaryButton } from '@tymbe/ui-primitives';
import TySelectRequirements from '../../../../components/inputs/TySelectRequirements';
import TySelectUtility from '../../../../components/inputs/TySelectUtility/TySelectUtility';
import { MomentInput } from 'moment';
import {
  ApplicationData,
  DocumentTypeData,
  isDocumentTypeData,
  isPerkData,
  ManShiftData,
  PerkData,
  PerkGroup,
  PerkId,
  ShiftData,
} from '../../../../types/TymbeApi';
import { ContestDuration, Gender, ShiftType } from '@tymbe/schema/enums';
import { getDurationString, maxDuration, queryFn } from './utils';
import TySelectInvitations from '../../../shift/create/components/TySelectInvitations/TySelectInvitations';
import { ShiftItem } from './types';
import CustomRange from './CustomRange';
import Tab from './Tab';
import { ErrorAlert, SuccessAlert } from '../../../../components/alerts';
import { checkIsAdultShift, getRequirements, isMedicalExaminationShift } from '../../../../utils/shift';
import { useUser } from '../../../../apiClient/ApiContext';
import { BadgePill } from '@tymbe/ty-components/indicators';
import { getPerkNameById } from '../../../../utils';
import { FilterOptionOption } from 'react-select';
import { Roles } from '../../../../utils/enums';
import { isDateBeforeSixthOfNextMonth } from '@tymbe/utils/time';
import TymberNo from './TymberNo';
import ShiftCreateItem from './ShiftCreateItem';
import ShiftListItem from './ShiftItem';
import classNames from 'classnames';
import ReactGA from 'react-ga4';
import LongLoader from '../../../../components/LongLoader';
import { validateContactInfo } from '../../../shift/create/components/ShiftForm';
import { ContestDurationSettings, ContestDurationType } from './ContestDurationSettings';
import { informedConfig } from '../../../../config/informed';
import { isDayHoliday } from '../../../../utils/holiday';
import Tooltip from 'rc-tooltip';
import { isSafari } from '../../../../utils/safari';
import { TYMBE_CONTRACTOR_ID } from '../../../../utils/constants';

type ShiftCreateProps = {
  departmentId: BranchofficeData['id'];
  onPublish?: () => void;
  onCancel?: () => void;
};

interface NewShiftForm extends Omit<ShiftItem, 'date'> {
  index?: number;
  startDates: MomentInput[];
}

interface ShiftsForm {
  shifts: ShiftItem[];
}

const ShiftCreate = ({
  departmentId,
  onPublish,
  onCancel,
}: ShiftCreateProps) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [hasShifts, setHasShifts] = useState(false);
  const formApiRef = useRef<FormApi<ShiftsForm>>(undefined);
  const newShiftFormApiRef = useRef<FormApi<NewShiftForm>>(undefined);
  const invitationFormApiRef = useRef<FormApi<any>>(undefined);
  const shiftAddRef = useRef<(value: ShiftItem) => void>(undefined);
  const [implicitRequirements, setImplicitRequirements] = useState<
    (PerkData | DocumentTypeData)[]
  >([]);
  const user = useUser();

  const formApi = formApiRef.current;
  const newShiftFormApi = newShiftFormApiRef.current;
  const invitationFormApi = invitationFormApiRef.current;

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [sexDisabled, setSexDisabled] = useState(false);

  const { data: department } = useQuery({
    queryKey: ['branchoffice', departmentId] as const,
    queryFn,
  });

  const { data: company } = useQuery({
    queryKey: ['company', department?.company_id, { $eager: '[contractor, defaultSettings]' }] as const,
    queryFn,
    enabled: !!department,
  });

  const { data: branchoffice } = useQuery({
    queryKey: ['branchoffice', department?.parent_id] as const,
    queryFn,
    enabled: !!department,
  });

  // TySelectShiftTemplate
  const { data: templates } = useQuery({
    queryKey: ['shift-template', 'select'] as const,
    queryFn: async () => {
      let query: Query = {
        company_id: company?.id,
        $eager: '[perk, documentType, branchoffice, utility]',
        $modify: { branchoffice: [department?.id] },
      };

      query = {
        ...query,
        $and: [
          { publish_at: { $null: false } },
          { publish_at: { $lt: moment().toISOString() } },
        ],
      };

      const branchTemp = getResponseArray(
        await feathersClient.service('shift-template').find({ query })
      );
      const globalTemp = getResponseArray(
        await feathersClient
          .service('shift-template')
          .find({ query: { ...query, $modify: 'companyGlobal' } })
      );

      return [...branchTemp, ...globalTemp];
    },
    enabled: !!company?.id && !!department?.id,
  });

  const createShift = useMutation({
    mutationKey: ['shift'],
    mutationFn: async (data: Partial<ShiftData> | Partial<ShiftData>[]) => {
      return feathersClient
        .service('shift')
        .create(data, { query: { $eager: 'manShift.perk' } });
    },
  });

  const checkInvitation = useMutation({
    mutationKey: ['invite-check'],
    mutationFn: async (data: any) => {
      if (!data || !data.length) {
        return [];
      }
      let result: any[] = [];
      while (data.length) {
        const batch = data.splice(0, 20);
        const res = getResponseArray(await feathersClient.service('invite-check').create(batch));
        result = result.concat(res);
      }
      return result;
    },
  });

  const createInvitation = useMutation({
    mutationKey: ['application'],
    mutationFn: async (data: Partial<ApplicationData>[]) => {
      const url = window.location.href;
      const content_group = window.location.pathname.split('/')[1];

      const res = await feathersClient.service('application').create(data);

      ReactGA.event('created_invitation', {
        url,
        content_group,
        count: data.length,
      });

      return res;
    },
    onSuccess: async (data) => {
      if (!data || !data.length) {
        return;
      }
    }
  });

  const getShiftTemplate = (id?: string | number) => {
    return templates?.find((template) => template.id === Number(id));
  };

  const getImplicitRequirements = (
    startTime?: MomentInput,
    endTime?: MomentInput
  ) => {
    const implicitRequirements: (PerkData | DocumentTypeData)[] = [];
    if (checkIsAdultShift(startTime, endTime)) {
      implicitRequirements.push({
        id: PerkId.ADULT,
        group: PerkGroup.AGE,
      } as PerkData);
    }
    if (
      // DISCLAIMER: This is due to TD-2407. In first stage we need to test this functionality on our (113) company.
      // Therefore it's fixed for the 113 constant.
      // It should be expanded later to all DPC contracts? (or the SERVICE fullfilment type companies?)
      company?.contractor?.some((contractor) => contractor.id === TYMBE_CONTRACTOR_ID) &&
      isMedicalExaminationShift(startTime, endTime)
    ) {
      implicitRequirements.push({
        id: PerkId.MEDICAL_EXAMINATION,
        group: null,
      } as PerkData);
    }

    return implicitRequirements;
  };

  const getDisabledDate = (date: moment.Moment) => {
    if (user.role?.some((role) => Roles.SUPER_ADMIN === role.slug)) {
      if (company?.is_readonly) {
        return date.isAfter(moment());
      }
      return false;
    }
    if (user.role?.some((role) => Roles.TYMBE_ADMIN === role.slug)) {
      return !isDateBeforeSixthOfNextMonth(date);
    }
    return date.isBefore(moment(), 'day');
  };

  const onAddShifts = ({ values, ...other }: FormState<NewShiftForm>) => {
    const { startDates = [], index, ...rest } = values;

    if (index !== undefined) {
      const initVal = formApi?.getValue<any>(`shifts[${index}]`);
      formApi?.setValue<any>(`shifts[${index}]`, {
        ...initVal,
        ...rest,
        date: startDates[0],
      });
    } else {
      for (const date of startDates) {
        shiftAddRef.current?.({
          ...rest,
          date: date,
        });
      }
    }
    newShiftFormApi?.reset();
    setCurrentTab(1);
  };

  const onEditShift = (shift: ShiftItem, index: number) => {
    setCurrentTab(2);
    newShiftFormApi?.setValues({
      ...shift,
      startDates: [moment(shift.date)],
      index,
    });
  };

  const getInvitations = () => {
    const invitations = invitationFormApi?.getValue('persons') || [];
    return invitations;
  };

  const publishShifts = async ({ values }: FormState<ShiftsForm>) => {
    const { shifts = [] } = values;
    const newShifts: Partial<ShiftData>[] = [];
    const invitations: { person_id: number; shifts: Partial<ShiftData>[] }[] = [];

    const company_id = company?.id;
    const branchoffice_id = department?.id;

    if (!company_id || !branchoffice_id) {
      return;
    }

    const now = moment();

    for (const shift of shifts) {
      if (!shift.templateId) continue;

      if (Number(shift.slots) < Number(shift.invitations?.length || 0)) {
        throw new Error('Počet pozvánek nesmí být větší než počet míst.');
      }

      const shiftTemplate = getShiftTemplate(shift.templateId);
      if (!shiftTemplate) {
        throw new Error('Nelze nalézt pracovní vzor');
      }

      // Company default settings are supposed to override manual changes
      if (company.defaultSettings?.shift_contest_end_variant && company.defaultSettings?.shift_contest_end_time) {
        shift.contestEndVariant = company.defaultSettings.shift_contest_end_variant;
        shift.contestEndTime = company.defaultSettings.shift_contest_end_time;
      }

      const [startHours, startMinutes] = shift.startTime.split(':').map(Number);
      const [endHours, endMinutes] = shift.endTime.split(':').map(Number);
      const addDay =
        startMinutes + startHours * 60 > endMinutes + endHours * 60 ? 1 : 0;

      const start_time = moment(shift.date)
        .set({ hour: startHours, minute: startMinutes })
        .startOf('minute')
      ;
      const end_time = moment(shift.date)
        .set({ hour: endHours, minute: endMinutes })
        .add(addDay, 'day')
        .startOf('minute')
      ;

      const manShifts: ManShiftData[] = [];

      if (shift.noPreferences) {
        for (let i = 0; i < Number(shift.slots); i++) {
          manShifts.push({} as ManShiftData);
        }
      } else if (shift.menOnly || shift.womenOnly) {
        for (let i = 0; i < Number(shift.slots); i++) {
          manShifts.push({
            perk: [
              {
                id: shift.menOnly ? PerkId.MAN_ONLY : PerkId.WOMAN_ONLY,
              } as PerkData,
            ],
          } as ManShiftData);
        }
      } else {
        const totalCount = Number(shift.slots);
        const menCount = Number(shift.men) || 0;
        const womenCount = Number(shift.women) || 0;

        for (let i = 0; i < totalCount; i++) {
          if (i < menCount) {
            manShifts.push({
              perk: [{ id: PerkId.MAN_ONLY } as PerkData],
            } as ManShiftData);
          } else if (i < menCount + womenCount) {
            manShifts.push({
              perk: [{ id: PerkId.WOMAN_ONLY } as PerkData],
            } as ManShiftData);
          } else {
            manShifts.push({} as ManShiftData);
          }
        }
      }

      const perks = new Map();
      const docTypes = new Map();

      const implicitRequirements = getImplicitRequirements(
        start_time,
        end_time
      );

      shiftTemplate.perk?.forEach((perk) => {
        perks.set(perk.id, perk);
      });
      shiftTemplate.documentType?.forEach((docType) => {
        docTypes.set(docType.id, docType);
      });

      implicitRequirements.filter(isDocumentTypeData).forEach((docType) => {
        docTypes.set(docType.id, docType);
      });
      implicitRequirements.filter(isPerkData).forEach((perk) => {
        perks.set(perk.id, perk);
      });

      shift.requirements?.filter(isDocumentTypeData).forEach((docType) => {
        docTypes.set(docType.id, docType);
      });
      shift.requirements?.filter(isPerkData).forEach((perk) => {
        perks.set(perk.id, perk);
      });

      let contest_start = moment.min(now, start_time);
      let contest_end = moment.max(
        contest_start,
        start_time.clone().subtract(1, 'hour')
      );

      if (shift.contestStart) {
        const { contestStart } = getOldestShift(shifts)!;
        contest_start = contestStart;
      }

      if (shift.contestEndVariant) {
        // const { date, startTime } = getOldestShift(shifts)!;
        switch (shift.contestEndVariant) {
          case ContestDurationType.hoursBeforeAt:
            contest_end = moment(shift.date)
              .set({ hour: Number(shift.startTime!.split(':')[0]), minute: 0 })
              .subtract(shift.contestEndTime, 'hours');
            break;
          case ContestDuration.DAY_OF_SHIFT_BEFORE_AT:
            contest_end = moment(shift.date)
              .hours(Number(shift.contestEndTime!.split(':')[0]))
              .minutes(Number(shift.contestEndTime!.split(':')[1]));
            break;
          case ContestDuration.DAY_BEFORE_AT:
            contest_end = moment(shift.date!)
              .subtract(1, 'day')
              .hours(Number(shift.contestEndTime!.split(':')[0]))
              .minutes(Number(shift.contestEndTime!.split(':')[1]));
            break;
          case ContestDuration.WORKDAY_BEFORE_AT:
            contest_end = getNearestWorkday(shift.date!, true)
              .hours(Number(shift.contestEndTime!.split(':')[0]))
              .minutes(Number(shift.contestEndTime!.split(':')[1]));
            break;
          case ContestDuration.NON_HOLIDAY_WORKDAY_BEFORE_AT:
            contest_end = getNearestWorkday(shift.date!, false)
            .hours(Number(shift.contestEndTime!.split(':')[0]))
            .minutes(Number(shift.contestEndTime!.split(':')[1]));
            break;
          default:
            break;
        }
      }

      const shiftData: Partial<ShiftData> = {
        shift_template_id: shift.templateId,
        company_id,
        branchoffice_id,
        position_id: shiftTemplate.position_id,
        contact_person: shift.contact,
        name: shiftTemplate.name || shiftTemplate.template_name,
        payment_base: Number(shiftTemplate.payment_base),
        credits_min: Number(shiftTemplate.credits_min),
        credits: Number(shiftTemplate.credits),
        invitation_credits: Number(shiftTemplate.invitation_credits),
        billing_rate: Number(shiftTemplate.billing_rate),
        margin: Number(shiftTemplate.margin),
        contest_start: contest_start.toISOString(),
        contest_end: contest_end.toISOString(),
        start_time: start_time.toISOString(),
        end_time: end_time.toISOString(),
        description: shiftTemplate.description,
        instruction: shiftTemplate.instruction,
        instruction_newcomers: shiftTemplate.instruction_newcomers,
        pay_supplement: shiftTemplate.pay_supplement,
        job_classification_ids: shiftTemplate.job_classification_ids,
        expected_hpp_weekly_hours: shiftTemplate.expected_hpp_weekly_hours,
        maximum_hpp_trial_period: shiftTemplate.maximum_hpp_trial_period,
        type: ShiftType.REGULAR,
        emoji: shiftTemplate.emoji,

        manShift: manShifts,
        documentType: Array.from(docTypes.values()).map(
          ({ id }) => ({ id } as DocumentTypeData)
        ),
        perk: Array.from(perks.values()).map(({ id }) => ({ id } as PerkData)),
        labels: shiftTemplate.labels
      };

      (shift.invitations || []).forEach((invitation) => {
        const existingInv = invitations.find((i) => i.person_id === Number(invitation.id));
        if (existingInv) {
          existingInv.shifts.push(shiftData);
        } else {
          invitations.push({
            person_id: Number(invitation.id),
            shifts: [shiftData],
          });
        }
      });

      newShifts.push(shiftData);
    }

    await checkInvitation.mutateAsync(invitations);

    const createdShifts = getResponseArray(
      await createShift.mutateAsync(newShifts)
    );

    ReactGA.event('added_shift', {
      url: window.location.href,
      count: createdShifts.length,
    });

    const applications = createdShifts.flatMap((shift, id) => {
      if (!shifts[id]?.invitations?.length) {
        return [];
      }

      const manShifts = shift.manShift || [];
      const forMan = manShifts.filter((manShift) =>
        manShift.perk?.find((perk) => perk.id === PerkId.MAN_ONLY)
      );
      const forWoman = manShifts.filter((manShift) =>
        manShift.perk?.find((perk) => perk.id === PerkId.WOMAN_ONLY)
      );
      const forOthers = manShifts.filter((manShift) =>
        manShift.perk?.every(
          (perk) => ![PerkId.MAN_ONLY, PerkId.WOMAN_ONLY].includes(perk.id)
        )
      );

      return shifts[id].invitations.map(async (invitation) => {
        const personData = await feathersClient
          .service('person-data')
          .get(invitation.id);
        const manShift =
          (personData.gender === Gender.MALE ? forMan.pop() : forWoman.pop()) ||
          forOthers.pop();

        if (!manShift) {
          throw new Error(
            'Snažíte se pozvat více brigádníků než je volných míst.'
          );
        }

        return {
          credits: Number(shift.invitation_credits),
          payment_base: Number(shift.payment_base),
          invitation: true,
          state: null,
          person_id: Number(invitation.id),
          man_shift_id: manShift.id,
          employer_id: shift.company_id,
        } satisfies Partial<ApplicationData>;
      });
    });

    await createInvitation.mutateAsync(await Promise.all(applications));
  };

  const onSubmit = async (state: FormState<ShiftsForm>) => {
    setSubmitting(true);
    try {
      await publishShifts(state);
      onPublish?.();
      SuccessAlert('Směny byly úspěšně publikovány.');
    } catch (e) {
      ErrorAlert(String(e));
    }
    setSubmitting(false);
  };

  const enforceSexPerk = (perk?: PerkData) => {
    switch (perk?.id) {
      case PerkId.MAN_ONLY:
        newShiftFormApi?.setValue('noPreferences', false);
        newShiftFormApi?.setValue('womenOnly', false);
        newShiftFormApi?.setValue('menOnly', true);
        setSexDisabled(true);
        break;
      case PerkId.WOMAN_ONLY:
        newShiftFormApi?.setValue('noPreferences', false);
        newShiftFormApi?.setValue('menOnly', false);
        newShiftFormApi?.setValue('womenOnly', true);
        setSexDisabled(true);
        break;
      default:
        setSexDisabled(false);
    }
  };

  // keep in sync with getLatestRegistration
  const getNearestWorkday = (
    shiftStartDay: moment.MomentInput,
    allowHoliday = true
  ) => {
    const shiftContestEndDay = moment(shiftStartDay).subtract(1, 'day');

    const isWeekend = [0, 6].includes(shiftContestEndDay.day());
    const isHoliday = isDayHoliday(shiftContestEndDay);

    // Allow holidays if flag is true
    if (!isWeekend && (allowHoliday || !isHoliday)) {
      return shiftContestEndDay;
    }

    return getNearestWorkday(shiftContestEndDay, allowHoliday);
  };

  const getOldestShift = (shifts: ShiftItem[]) => {
    if (!shifts?.length) return null;
    return shifts.sort((oldest: ShiftItem, current: ShiftItem) => {
      return moment(oldest.date).valueOf() - moment(current.date).valueOf();
    })[0];
  };

  const onNewShiftChange = ({ values }: FormState<NewShiftForm>) => {
    const { startTime, endTime, templateId } = values;

    const requirements: Map<
      string | number,
      PerkData | DocumentTypeData
    > = new Map();
    const shiftTemplate = getShiftTemplate(templateId);
    shiftTemplate?.documentType?.forEach((doc) =>
      requirements.set(doc.id, doc)
    );
    shiftTemplate?.perk?.forEach((perk) =>
      requirements.set(perk.id, perk)
    );


    if (startTime && endTime) {
      const [startHours, startMinutes] = startTime
        .split(':')
        .map(Number);
      const [endHours, endMinutes] = endTime
        .split(':')
        .map(Number);
      const addDay =
        startMinutes + startHours * 60 >
        endMinutes + endHours * 60
          ? 1
          : 0;

      const now = moment();

      const start_time = now
        .clone()
        .set({ hour: startHours, minute: startMinutes });
      const end_time = now
        .clone()
        .set({ hour: endHours, minute: endMinutes })
        .add(addDay, 'day');

      const implicitRequirements = getImplicitRequirements(
        start_time,
        end_time
      );

      implicitRequirements.forEach((doc) =>
        requirements.set(doc.id, doc)
      );
    }

    const implicitRequirements = Array.from(
      requirements.values()
    );
    setImplicitRequirements(implicitRequirements);
  }

  return (
    <div className="w-full h-full flex flex-col">
      <LongLoader isLoading={submitting}>
        Směny se publikují, prosím čekejte.
      </LongLoader>
      <div className="p-6 flex align-center border-b border-secondary-100 text-heading-4-xl">
        Vypsat směny
      </div>

      {/* padding wrapper */}
      <div className="p-4 flex flex-col md:grid grid-cols-[440px_1fr] grid-rows-[min-content_min-content_1fr] md:overflow-y-auto h-full md:h-auto">
        {/* shift detail breadcrumbs */}
        <ul className="flex gap-2 mb-4 col-start-1 col-end-2">
          <li className="truncate md:h-fit">{company?.name}</li>
          <li className="md:h-fit">
            <FavouriteIcon
              name="chevron_right"
              className="size-4 p-0.5 bg-secondary-transparent-5"
            />
          </li>
          <li className="truncate md:h-fit">{branchoffice?.name}</li>
          <li className="md:h-fit">
            <FavouriteIcon
              name="chevron_right"
              className="size-4 p-0.5 bg-secondary-transparent-5"
            />
          </li>
          <li className="truncate md:h-fit">{department?.name}</li>
        </ul>

        {/* tabs container */}
        <div className="px-4 pt-4 bg-secondary-50 rounded-t-3xl col-start-1 col-end-2">
          {/* tabs */}
          <div className="flex overflow-x-auto border-b-[1px] border-secondary-100">
            <Tab
              onClick={() => setCurrentTab(0)}
              active={currentTab === 0}
              disabled={currentTab === 2}
            >
              Nové směny
            </Tab>
            <Tab
              title={'Nejprve vytvořte směny'}
              onClick={() => setCurrentTab(1)}
              disabled={!hasShifts || currentTab === 2}
              active={currentTab === 1}
            >
              Pozvat Tymbery
            </Tab>
            <Tab disabled={currentTab !== 2} active={currentTab === 2}>
              Upravit směnu
            </Tab>
          </div>
        </div>

        {/* tabs content new shift form */}

        {/* new shift form */}
        <Form<NewShiftForm>
          className="pt-6 flex flex-col gap-2 md:pb-0 px-4 pb-4 bg-secondary-50 rounded-b-3xl col-start-1 col-end-2 md:mb-4 md:overflow-y-auto md:relative"
          formApiRef={newShiftFormApiRef}
          onSubmit={onAddShifts}
          hidden={![0, 2].includes(currentTab)}
          validateOn="change"
          onChange={onNewShiftChange}
        >
          <TyInput name="index" className="hidden" />

          {/* position and date responsive container */}
          <div className="flex flex-col sm:flex-row gap-2 md:flex-col">
            {/* position */}
            <div className="flex gap-2 items-center sm:w-1/2 md:w-full">
              <PlacesIcon name="business_center" className="size-5" />
              <TySelectInput<number>
                name="templateId"
                className="grow"
                required={informedConfig.errorMessage.required}
                onChange={({ value }) => {
                  const shiftTemplate = getShiftTemplate(value);

                  const sexPerk = shiftTemplate?.perk?.find((req) => req.group === PerkGroup.SEX);
                  enforceSexPerk(sexPerk);
                  if (!sexPerk && !newShiftFormApi?.getValue('noPreferences')) {
                    newShiftFormApi?.setValue('noPreferences', true);
                    newShiftFormApi?.setValue('menOnly', false);
                    newShiftFormApi?.setValue('womenOnly', false);
                  }
                }}
              >
                {templates?.map((template) => (
                  <TyOption
                    key={template.id}
                    value={template.id}
                    label={template.template_name}
                  >
                    {template.template_name}
                  </TyOption>
                ))}
              </TySelectInput>
            </div>

            {/* date */}
            <div className="flex gap-2 items-center sm:w-1/2 md:w-full">
              <EditorIcon
                name="insert_invitation"
                className="size-5 shrink-0"
              />
              <TyDateMultiPicker
                name="startDates"
                required={informedConfig.errorMessage.required}
                highlightHolidays
                disabledDate={getDisabledDate}
                onChange={({ value }) => {
                  if (value.length > 1 && currentTab === 2) {
                    newShiftFormApi?.setValue('startDates', [value[1]]);
                  }
                }}
              />
            </div>
          </div>

          {/* time */}
          <div className="flex gap-2 items-center">
            <DeviceIcon name="access_time_filled" className="size-5 shrink-0" />
            {/* time from - to */}
            <div className="flex gap-2 items-center sm:flex-grow">
              <TyTimeInput
                name="startTime"
                validateWhen={['endTime']}
                validate={maxDuration}
                required={informedConfig.errorMessage.required}
                validateOn="change"
              />
              -
              <TyTimeInput
                name="endTime"
                validateWhen={['startTime']}
                validate={maxDuration}
                required={informedConfig.errorMessage.required}
                validateOn="change"
              />
              <FormStateAccessor<NewShiftForm>>
                {(formState) => (
                  <span className="text-nowrap">
                    {getDurationString(
                      formState.values.startTime,
                      formState.values.endTime
                    )}
                  </span>
                )}
              </FormStateAccessor>
            </div>
          </div>

          {/* contact */}
          <div className="flex gap-2 items-center">
            <CommunicationIcon name="phone" className="size-5 shrink-0" />
            <TyInput name="contact" required={informedConfig.errorMessage.required} validate={validateContactInfo} />
          </div>

          <div className="text-body-2-md font-semibold">Místa</div>

          {/* number of people */}
          <div className="flex flex-col">
            <div className="flex gap-2 items-center sm:w-1/2">
              <ActionIcon
                name="supervisor_account"
                className="size-5 shrink-0"
              />
              <TyNumberInput name="slots" min={0} required={informedConfig.errorMessage.required} />
            </div>

            <div className="flex gap-2 items-center my-2">
              <TyCheckbox
                name="noPreferences"
                onChange={({ value }) => {
                  if (value) {
                    newShiftFormApi?.setValue('menOnly', false);
                    newShiftFormApi?.setValue('womenOnly', false);
                    newShiftFormApi?.setValue('men', '0');
                    newShiftFormApi?.setValue('women', '0');
                  }
                }}
                disabled={sexDisabled}
              />
              <span className="text-body-2-xl text-secondary-500">
                Nemám preferenci
              </span>
            </div>
          </div>

          {/* min number of men/women */}
          <FormStateAccessor<NewShiftForm>>
            {({ values }) => {
              const men = Number(values.men) || 0;
              const women = Number(values.women) || 0;
              const slots = Number(values.slots) || 0;

              let menMax = slots - women;
              let womenMax = slots - men;

              if (men >= women && men + women > slots) {
                womenMax = slots - Math.min(menMax, slots - men);
              } else if (men < women && men + women > slots) {
                menMax = slots - Math.min(womenMax, slots - women);
              }

              return (
                <div className="flex flex-col sm:flex-row gap-2">
                  <CustomRange
                    className="sm:w-1/2 md:h-full"
                    name="men"
                    label="Minimální počet mužů"
                    icon={<FavouriteIcon name="male" className="size-5" />}
                    max={menMax}
                    disabled={
                      values.noPreferences || values.womenOnly || sexDisabled
                    }
                    suffix={
                      <TyCheckbox
                        name="menOnly"
                        label="Pouze muži"
                        disabled={
                          values.noPreferences ||
                          values.womenOnly ||
                          sexDisabled
                        }
                      />
                    }
                    validateOn="change"
                  />
                  <CustomRange
                    className="sm:w-1/2 md:h-full"
                    name="women"
                    label="Minimální počet žen"
                    icon={<FavouriteIcon name="female" className="size-5" />}
                    max={womenMax}
                    disabled={
                      values.noPreferences || values.menOnly || sexDisabled
                    }
                    suffix={
                      <TyCheckbox
                        name="womenOnly"
                        label="Pouze ženy"
                        disabled={
                          values.noPreferences || values.menOnly || sexDisabled
                        }
                      />
                    }
                    validateOn="change"
                  />
                </div>
              );
            }}
          </FormStateAccessor>

          {/* additional settings section */}
          <Collapse
            className="flex flex-col gap-2"
            toggler={
              <Button
                className="border-[1px] border-secondary-300 text-caption-md font-semibold gap-2 py-1 px-3 flex items-center"
                type="button"
              >
                Další nastavení
                <FavouriteIcon
                  name="chevron_down"
                  className="size-5 [.collapse-open_&]:rotate-180"
                />
              </Button>
            }
          >
            <div className="flex flex-col gap-2">
              {/* requirements */}
              <div className="flex flex-wrap gap-1">
                {implicitRequirements.filter(isPerkData).map((perk) => (
                  <BadgePill variant="default">
                    {getPerkNameById(perk.id)}
                  </BadgePill>
                ))}
              </div>
              <div className="flex flex-wrap gap-1">
                {implicitRequirements
                  .filter(isDocumentTypeData)
                  .map((docType) => (
                    <BadgePill variant="default">{docType.name}</BadgePill>
                  ))}
              </div>

              {/* custom requirements select */}
              <div className="flex gap-2 items-center">
                <Tooltip overlay="Podmínky" placement="topLeft">
                  <CustomIcon name="label2" className="size-5 shrink-0" />
                </Tooltip>
                <TySelectRequirements
                  name="requirements"
                  className="grow [&_.ty\_select\_\_menu]:-mx-9 [&_.ty\_select\_\_menu]:-mt-2"
                  menuPosition="fixed"
                  menuPlacement='top'
                  companyId={company?.id}
                  userRole={user.role}
                  filterOption={({ data }: FilterOptionOption<any>) => {
                    if (isDocumentTypeData(data) && data.name !== 'Student') {
                      return false;
                    }
                    return !getRequirements(
                      getShiftTemplate(newShiftFormApi?.getValue('templateId')),
                      user.role
                    ).some((x) =>
                      isPerkData(x) ? x.group === data.group : x.id === data.id
                    );
                  }}
                  isClearable={false}
                  isMulti
                  onChange={({ value }) => {
                    const selected = value as (PerkData | DocumentTypeData)[];
                    if (selected === undefined) return;

                    const sexPerk = selected
                      .filter(isPerkData)
                      .find((perk) => (perk.group = PerkGroup.SEX));
                    enforceSexPerk(sexPerk);
                  }}
                />
              </div>

              {/* shift visibility to tymbers */}
              <div>
                <FormStateAccessor<NewShiftForm>>
                  {({ values }) => {
                    const userPerks =
                      values.requirements?.filter(isPerkData) || [];
                    return (
                      <TymberNo
                        perks={[
                          ...implicitRequirements
                            .filter(isPerkData)
                            .map((p) => p.id),
                          ...userPerks.filter(isPerkData).map((p) => p.id),
                        ]}
                        companyId={company?.id}
                        departmentId={departmentId}
                        templateId={values.templateId}
                      />
                    );
                  }}
                </FormStateAccessor>
              </div>

              <div className="flex gap-2 items-center relative">
                <Tooltip overlay="Pomůcky" placement="topLeft">
                  <MapsIcon name="dry_cleaning" className="size-5 shrink-0" />
                </Tooltip>
                <TySelectUtility
                  name="utilities"
                  className="grow [&_.ty\_select\_\_menu]:-mx-9 [&_.ty\_select\_\_menu]:-mt-2"
                  menuPosition="fixed"
                  isMulti
                />
              </div>

              <ContestDurationSettings companyDefaultSettings={company?.defaultSettings} />
            </div>
          </Collapse>

          {/* reset and add buttons */}
          <FormStateAccessor<NewShiftForm>>
            {({ values }) => (
              <div className="md:sticky md:bottom-0 md:left-0 md:right-0 md:p-4 md:bg-secondary-50 z-30">
                <div
                  className="flex gap-3 items-center justify-between md:mt-auto"
                  hidden={values.index !== undefined}
                >
                  <Button className="border gap-2" type="reset">
                    <AvIcon name="replay" className="size-4" />
                    Resetovat
                  </Button>
                  <Button className="gap-2 bg-secondary-600 text-white">
                    <ImageIcon name="add_to_photos" className="size-5" /> Přidat
                    směny
                  </Button>
                </div>
                <div className="flex gap-3" hidden={values.index === undefined}>
                  <Button
                    className="border gap-2"
                    type="reset"
                    onClick={() => setCurrentTab(1)}
                  >
                    <FavouriteIcon name="close" className="size-4" /> Zahodit
                    změny
                  </Button>
                  <Button className="gap-2 bg-secondary-600 text-white">
                    <FavouriteIcon name="check" className="size-4" /> Uložit
                    úpravy
                  </Button>
                </div>
              </div>
            )}
          </FormStateAccessor>
        </Form>

        {/* tabs content invitation form */}
        <Form<ShiftsForm>
          className="px-4 pb-4 bg-secondary-50 flex flex-col rounded-b-3xl pt-6 gap-4 col-start-1 col-end-2 md:mb-[80px]"
          formApiRef={invitationFormApiRef}
          hidden={currentTab !== 1}
        >
          {/* info text */}
          <div className="flex gap-2 items-start">
            <ActionIcon name="info" className="size-4 shrink-0" />
            <div className="text-body-2-xl text-secondary-500">
              Níže si vytvořte seznam všech Tymberů, se kterými chcete pracovat.
              Na směny pak budete moct přidat pracovníky jednotlivě.
            </div>
          </div>

          {/* invitation select */}
          <div className="flex gap-2 items-center">
            <ActionIcon name="supervisor_account" className="size-4 shrink-0" />
            <TySelectInvitations
              name="persons"
              className={classNames("w-full",
                {'[&_.new-menu]:-mx-9 [&_.new-menu]:-mt-2':!isSafari},
                {'[&_.new-menu]:-ml-[13px] [&_.new-menu]:mt-4':isSafari}
              )}
              companyId={company?.id}
              branchOfficeId={branchoffice?.id}
              departmentId={department?.id}
              isMulti
              menuPosition="fixed"
            />
          </div>

          {/* clear button */}
          <SecondaryButton
            variant="outlined-error"
            type="reset"
            className="!bg-transparent gap-1 ml-auto"
          >
            <ActionIcon name="delete_outline" className="size-4" />
            <span className="text-caption-md font-semibold">Vyčistit</span>
          </SecondaryButton>
        </Form>

        {document.querySelector('html')!.clientWidth >= 1280 ? (
          <div className="h-full grow [scrollbar-gutter:stable] overflow-x-auto scrollbar-visible hidden md:block grid-cols-2 sm:gap-2 row-start-1 row-span-3 col-start-2">
            <Form
              className={classNames(
                'grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-10 items-center font-medium text-body-2-xl pr-4 ml-10',
                hasShifts && 'min-w-[950px]'
              )}
              formApiRef={formApiRef}
              onSubmit={onSubmit}
              id="newShiftsForm"
            >
              <FormStateAccessor<ShiftsForm>>
                {({ values }) => {
                  setHasShifts(() => !!values.shifts?.length);
                }}
              </FormStateAccessor>

              {formApi?.getValue('shifts') !== undefined ? (
                <div className="grid grid-cols-subgrid col-span-5 px-4 auto-rows-[theme('spacing.16')] items-center">
                  <div>Datum</div>
                  <div>Čas</div>
                  <div>Míst</div>
                  <div>Pozvánky</div>
                </div>
              ) : null}

              <div className="contents">
                <ArrayField name="shifts">
                  {({ addWithInitialValue, ...rest }) => {
                    shiftAddRef.current = addWithInitialValue;

                    return (
                      <ArrayField.Items>
                        {() => {
                          return (
                            <ShiftListItem
                              invitationsRef={invitationFormApiRef}
                              onEdit={onEditShift}
                              getInvitations={getInvitations}
                              getShiftTemplate={getShiftTemplate}
                            />
                          );
                        }}
                      </ArrayField.Items>
                    );
                  }}
                </ArrayField>
              </div>
            </Form>
          </div>
        ) : (
          <Form
            formApiRef={formApiRef}
            onSubmit={onSubmit}
            id="newShiftsForm"
            className="mt-4 mb-4 sm:grid grid-cols-2 sm:gap-2 row-start-1 row-span-3 col-start-2 md:hidden"
          >
            <FormStateAccessor<ShiftsForm>>
              {({ values }) => {
                setHasShifts(() => !!values.shifts?.length);
              }}
            </FormStateAccessor>

            {/* list of invited users */}
            <ArrayField name="shifts">
              {({ addWithInitialValue, ...rest }) => {
                shiftAddRef.current = addWithInitialValue;

                return (
                  <ArrayField.Items>
                    {() => {
                      return (
                        <ShiftCreateItem
                          invitationsRef={invitationFormApiRef}
                          onEdit={onEditShift}
                          getInvitations={getInvitations}
                          getShiftTemplate={getShiftTemplate}
                        />
                      );
                    }}
                  </ArrayField.Items>
                );
              }}
            </ArrayField>
          </Form>
        )}

        {/* form with inited users and shift items */}

        {/* form with inited users and shift items - desktop version */}

        {/* publish and cancel buttons */}
        <div className="flex items-center bg-secondary-100 p-4 mt-auto -mx-4 -mb-4 justify-end gap-2 col-span-2 md:hidden">
          <button
            type="button"
            className="py-2 px-4 border text-sm font-semibold border-secondary-300"
            onClick={() => {
              onCancel?.();
            }}
          >
            Zrušit
          </button>
          <ShiftPublishButton
            form="newShiftsForm"
            disabled={!hasShifts || submitting}
            isPublishing={submitting}
          />
        </div>
      </div>

      <div className="items-center bg-secondary-100 p-4 justify-end gap-2 hidden mt-auto md:flex">
        {/* CR what is this for? */}
        {/* <div
            className={classNames(
              'p-2 border border-error-500 rounded-md bg-error-100 text-error font-medium flex gap-4',
              { invisible: true }
            )}
          >
            {{}?.msg}
            <button
              type="button"
              className="p-0 rounded-none underline hover:text-error-600"
            >
              Detail
            </button>
          </div> */}
        <button
          type="button"
          className="py-2 px-4 border text-sm font-semibold border-secondary-300"
          onClick={() => {
            onCancel?.();
          }}
        >
          Zrušit
        </button>
        <ShiftPublishButton
          form="newShiftsForm"
          disabled={!hasShifts || submitting}
          isPublishing={submitting}
        />
      </div>
    </div>
  );
};

export default ShiftCreate;
